import { bytesToBase64, bytesToHex, decodeBase64, toArrayBuffer } from "./bytes";
import { PRIME_HKDF_INFO } from "./types";

const ECDH_CURVE = "P-256";
const HKDF_INFO = new TextEncoder().encode(PRIME_HKDF_INFO);

function sec1ToPkcs8(sec1Der: Uint8Array): Uint8Array {
  const oidEc = Uint8Array.from([0x06, 0x07, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x02, 0x01]);
  const oidP256 = Uint8Array.from([0x06, 0x08, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x03, 0x01, 0x07]);
  const algoSeq = new Uint8Array(2 + oidEc.length + oidP256.length);
  algoSeq[0] = 0x30;
  algoSeq[1] = oidEc.length + oidP256.length;
  algoSeq.set(oidEc, 2);
  algoSeq.set(oidP256, 2 + oidEc.length);
  const privKeyOctetLen = sec1Der.length < 128 ? 2 + sec1Der.length : 3 + sec1Der.length;
  const privKeyOctet = new Uint8Array(privKeyOctetLen);
  privKeyOctet[0] = 0x04;
  if (sec1Der.length < 128) {
    privKeyOctet[1] = sec1Der.length;
    privKeyOctet.set(sec1Der, 2);
  } else {
    privKeyOctet[1] = 0x81;
    privKeyOctet[2] = sec1Der.length & 0xff;
    privKeyOctet.set(sec1Der, 3);
  }
  const version = Uint8Array.from([0x02, 0x01, 0x00]);
  const innerLen = version.length + algoSeq.length + privKeyOctet.length;
  const outerLenBytes = innerLen < 128 ? 1 : 2;
  const pkcs8 = new Uint8Array(1 + outerLenBytes + innerLen);
  let offset = 0;
  pkcs8[offset++] = 0x30;
  if (innerLen < 128) {
    pkcs8[offset++] = innerLen;
  } else {
    pkcs8[offset++] = 0x81;
    pkcs8[offset++] = innerLen & 0xff;
  }
  pkcs8.set(version, offset);
  offset += version.length;
  pkcs8.set(algoSeq, offset);
  offset += algoSeq.length;
  pkcs8.set(privKeyOctet, offset);
  return pkcs8;
}

function decodeClientPublicKey(clientPublicKeyEncoded: string): Uint8Array {
  const trimmed = clientPublicKeyEncoded.trim();
  if (/^[0-9a-fA-F]{130}$/.test(trimmed)) {
    return Uint8Array.from(
      trimmed.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16)) ?? [],
    );
  }
  return decodeBase64(trimmed);
}

export async function deriveAesKeyFromECDH(
  serverPublicKeyBase64: string,
): Promise<{ clientPublicKey: string; aesKeyHex: string }> {
  const keyPair = await crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: ECDH_CURVE },
    true,
    ["deriveBits"],
  );
  const serverPublicKey = await crypto.subtle.importKey(
    "spki",
    toArrayBuffer(decodeBase64(serverPublicKeyBase64)),
    { name: "ECDH", namedCurve: ECDH_CURVE },
    false,
    [],
  );
  const sharedSecret = await crypto.subtle.deriveBits(
    { name: "ECDH", public: serverPublicKey },
    keyPair.privateKey,
    256,
  );
  const hkdfKey = await crypto.subtle.importKey("raw", sharedSecret, { name: "HKDF" }, false, [
    "deriveBits",
  ]);
  const derivedBits = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0), info: HKDF_INFO },
    hkdfKey,
    256,
  );
  const clientPublicKeyRaw = await crypto.subtle.exportKey("raw", keyPair.publicKey);
  return {
    clientPublicKey: bytesToHex(new Uint8Array(clientPublicKeyRaw)),
    aesKeyHex: bytesToHex(new Uint8Array(derivedBits)),
  };
}

export async function deriveTransportKeyFromECDH(
  serverPrivateKeyPem: string,
  clientPublicKeyEncoded: string,
): Promise<string> {
  let pem = serverPrivateKeyPem.trim();
  if (!pem.includes("-----BEGIN")) {
    const decoded = new TextDecoder().decode(decodeBase64(pem));
    pem = decoded.includes("-----BEGIN") ? decoded : pem;
    if (!pem.includes("-----BEGIN")) {
      return deriveFromPkcs8(sec1ToPkcs8(decodeBase64(serverPrivateKeyPem)), clientPublicKeyEncoded);
    }
  }
  const match = pem.match(
    /-----BEGIN[^-]*-----[\s\r\n]*([A-Za-z0-9+/=\s]+)[\s\r\n]*-----END[^-]*-----/,
  );
  const contents = (match?.[1] ?? pem.replace(/-----BEGIN[^-]*-----/g, "").replace(/-----END[^-]*-----/g, ""))
    .replace(/\s/g, "");
  const keyDer = decodeBase64(contents);
  const pkcs8 = pem.includes("EC PRIVATE KEY") ? sec1ToPkcs8(keyDer) : keyDer;
  return deriveFromPkcs8(pkcs8, clientPublicKeyEncoded);
}

async function deriveFromPkcs8(
  pkcs8Der: Uint8Array,
  clientPublicKeyEncoded: string,
): Promise<string> {
  const serverPrivate = await crypto.subtle.importKey(
    "pkcs8",
    toArrayBuffer(pkcs8Der),
    { name: "ECDH", namedCurve: ECDH_CURVE },
    false,
    ["deriveBits"],
  );
  const clientPublic = await crypto.subtle.importKey(
    "raw",
    toArrayBuffer(decodeClientPublicKey(clientPublicKeyEncoded)),
    { name: "ECDH", namedCurve: ECDH_CURVE },
    false,
    [],
  );
  const sharedSecret = await crypto.subtle.deriveBits(
    { name: "ECDH", public: clientPublic },
    serverPrivate,
    256,
  );
  const hkdfKey = await crypto.subtle.importKey("raw", sharedSecret, { name: "HKDF" }, false, [
    "deriveBits",
  ]);
  const derivedBits = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0), info: HKDF_INFO },
    hkdfKey,
    256,
  );
  return bytesToHex(new Uint8Array(derivedBits));
}

export async function generateEcdhKeyPair(): Promise<{
  privateKeyPkcs8Base64: string;
  publicKeySpkiBase64: string;
}> {
  const keyPair = await crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: ECDH_CURVE },
    true,
    ["deriveBits"],
  );
  const pkcs8 = new Uint8Array(await crypto.subtle.exportKey("pkcs8", keyPair.privateKey));
  const spki = new Uint8Array(await crypto.subtle.exportKey("spki", keyPair.publicKey));
  return {
    privateKeyPkcs8Base64: bytesToBase64(pkcs8),
    publicKeySpkiBase64: bytesToBase64(spki),
  };
}

export function privateKeyPemFromPkcs8Base64(pkcs8Base64: string): string {
  const body = pkcs8Base64.replace(/(.{64})/g, "$1\n").trim();
  return `-----BEGIN PRIVATE KEY-----\n${body}\n-----END PRIVATE KEY-----`;
}
